/**
 * 对比两个对象的值是否完全相等
 */
export function isObjectValueEqual(a: Record<string, unknown>, b: Record<string, unknown>) {
  // 取对象a和b的属性名
  const aProps = Object.getOwnPropertyNames(a);
  const bProps = Object.getOwnPropertyNames(b);
  // 判断属性名的length是否一致
  if (aProps.length !== bProps.length) {
    return false;
  }
  // 循环取出属性名，再判断属性值是否一致
  for (const attrProp of aProps) {
    const propName = attrProp;
    if (a[propName] !== b[propName]) {
      return false;
    }
  }
  return true;
}
/**
 * 可视计算
 * @param {HTMLElement} target 目标元素
 * @returns 返回
 */
export function isVisible(el: HTMLElement): {
  visibility: boolean;
  observeTargets: HTMLElement[];
} {
  if (el.getBoundingClientRect().width === 0 || el.getBoundingClientRect().height === 0) {
    return { visibility: false, observeTargets: [el] };
  }
  let p = el;
  const observeTargetHidden = [];
  // 如果自身visibility不可见，则要追溯可能继承的父节点
  let isParentVisible = 'hidden' === getStyle(p, 'visibility');
  while (p && p.nodeType === 1) {
    if ('0' === getStyle(p, 'opacity')) {
      return { visibility: false, observeTargets: [p] };
    }
    if (isParentVisible && 'hidden' === getStyle(p, 'visibility')) {
      observeTargetHidden.push(p);
    } else {
      // 如果从某层开始父元素可见了，则不再推入观察列表
      isParentVisible = false;
    }
    p = p.parentNode as HTMLElement;
  }
  if (observeTargetHidden.length !== 0) {
    return { visibility: false, observeTargets: observeTargetHidden };
  }
  return { visibility: true, observeTargets: [] };
}
function getStyle(el: HTMLElement, property: keyof CSSStyleDeclaration) {
  return document.defaultView?.getComputedStyle(el, null)[property];
}

/**
 * 计算有效曝光比例
 * @param {HTMLElement} target 目标元素
 * @returns 返回有效曝光比例和遮蔽元素
 */
export function calOverlap(target: HTMLElement, isFirstInterction: boolean) {
  const efp = function (arr: number[]) {
    const [x, y] = arr;
    return document.elementFromPoint(x, y);
  };

  const points = getKeyPoint(target);
  // 记录有效遮挡元素
  const overlapElements: HTMLElement[] = [];
  for (const point of points) {
    // 如果最上面那层就是目标元素，说明该点没有被遮挡，就不要再考虑这个点了
    const topElement = efp(point) as HTMLElement;
    if (!topElement || target.contains(topElement)) {
      continue;
    }

    const overlapElementsOnePoint = getOverlapElements(target, topElement, point);
    overlapElementsOnePoint.forEach((item) => {
      if (!overlapElements.includes(item)) {
        overlapElements.push(item);
      }
    });
  }

  return {
    intersectionRatio: calOverlapRatio(target, overlapElements, isFirstInterction),
    overlapElements
  };
}

/**
 * 获取目标元素关心的点位
 */
function getKeyPoint(target: HTMLElement) {
  // 使用 getBoundingClientRect 获取该元素的位置
  const rect = target.getBoundingClientRect();
  const middleX = Math.floor(rect.left + rect.width / 2);
  const middleY = Math.floor(rect.top + rect.height / 2);
  // 取四角和中间五个点（四角考虑像素差向内缩进2像素）
  const points: [number, number][] = [
    [middleX, middleY],
    [rect.left, rect.top],
    [rect.right, rect.top],
    [rect.left, rect.bottom],
    [rect.right, rect.bottom]
  ];
  return points;
}

/**
 * 获取有效遮挡元素
 */
function getOverlapElements(target: HTMLElement, _topElement: HTMLElement, point: [number, number]) {
  let overlapElements: HTMLElement[] = [];

  const [x, y] = point;
  const outerElements = document.elementsFromPoint(x, y);

  // 某些特殊情况此处获取元素无自身，等一会再算一下
  if (outerElements.includes(target)) {
    overlapElements = getOverlapElementsByOuter(target, outerElements as HTMLElement[]);
  }
  return overlapElements;
}

function getOverlapElementsByOuter(target: HTMLElement, outerElements: HTMLElement[]) {
  const overlapElements: HTMLElement[] = [];

  for (let i = 0; i < outerElements.length; i++) {
    const ele = outerElements[i];
    if (target.contains(ele)) {
      break;
    }
    if (
      ele.parentNode !== outerElements[i + 1] &&
      isVisible(ele).visibility &&
      !overlapElements.includes(ele) &&
      ele.tagName !== 'HTML' &&
      ele.tagName !== 'BODY'
    ) {
      overlapElements.push(ele);
    }
  }
  return overlapElements;
}

function calOverlapRatio(target: HTMLElement, arr: HTMLElement[], isFirstInterction: boolean) {
  if (typeof window === 'undefined') {
    console.warn('不支持非浏览器环境');
    return 0;
  }
  // 如果没有覆盖元素，则直接上报，不用再计算了
  if (arr.length === 0 && isFirstInterction) {
    return -1;
  }
  // 目标元素
  const targetRect = target.getBoundingClientRect();
  // 视口
  const vWidth = window?.innerWidth || document.documentElement.clientWidth;
  const vHeight = window?.innerHeight || document.documentElement.clientHeight;
  // 计算目标元素的面积
  const targetArea = targetRect.width * targetRect.height;
  // 进行小矩形切割
  const arrXOrigin = [targetRect.left, targetRect.right, 0, vWidth];
  const arrYOrigin = [targetRect.top, targetRect.bottom, 0, vHeight];
  const overflowParentRect = findOverflowParent(target);
  if (overflowParentRect) {
    arrXOrigin.push(overflowParentRect.left, overflowParentRect.right);
    arrYOrigin.push(overflowParentRect.top, overflowParentRect.bottom);
  }
  arr.forEach((el) => {
    const { top, left, bottom, right } = el.getBoundingClientRect();
    arrXOrigin.push(left, right);
    arrYOrigin.push(top, bottom);
  });

  const arrX = sortUnique(arrXOrigin);
  const arrY = sortUnique(arrYOrigin);

  let areaExpo = 0;
  for (let i = 1; i < arrX.length; i++) {
    for (let j = 1; j < arrY.length; j++) {
      const area = (arrX[i] - arrX[i - 1]) * (arrY[j] - arrY[j - 1]);
      // pos=[x1,x2,y1,y2]
      const pos = [arrX[i - 1], arrX[i], arrY[j - 1], arrY[j]];
      // 有效曝光面积 = 在视口内 + 在目标元素内 + 不在重叠元素内
      if (
        isInViewport(pos) &&
        isInTarget(target, pos) &&
        isInOverflowParent(overflowParentRect, pos) &&
        !isInOverlap(arr, pos)
      ) {
        areaExpo += area;
      }
    }
  }

  const intersectionRatio = areaExpo / targetArea;
  // 考虑到window.innerWidth只能取整而可能导致有误差
  if (intersectionRatio < 0.0001) {
    return 0;
  }
  return intersectionRatio;
}

function findOverflowParent(target: HTMLElement) {
  let overflowParent = target.parentElement;
  const vWidth = window?.innerWidth || document.documentElement.clientWidth;
  const vHeight = window?.innerHeight || document.documentElement.clientHeight;
  const overflowParentRect = {
    top: 0,
    left: 0,
    right: vWidth,
    bottom: vHeight
  };

  while (overflowParent && overflowParent.tagName !== 'HTML' && overflowParent.tagName !== 'BODY') {
    if (
      'hidden' === getStyle(overflowParent, 'overflow') ||
      'scroll' === getStyle(overflowParent, 'overflow') ||
      'auto' === getStyle(overflowParent, 'overflow')
    ) {
      const { top, bottom, left, right } = overflowParent.getBoundingClientRect();
      overflowParentRect.top = Math.max(overflowParentRect.top, top);
      overflowParentRect.left = Math.max(overflowParentRect.left, left);
      overflowParentRect.right = Math.min(overflowParentRect.right, right);
      overflowParentRect.bottom = Math.min(overflowParentRect.bottom, bottom);
    }

    overflowParent = overflowParent.parentElement;
  }

  return overflowParentRect;
}

/**
 * 排序并去重
 */
function sortUnique(arr: number[]) {
  return Array.from(new Set(arr.sort((a, b) => a - b)));
}

function isInViewport(pos: number[]) {
  if (typeof window === 'undefined') {
    console.warn('不支持非浏览器环境');
    return;
  }
  const [x1, x2, y1, y2] = pos;
  const vWidth = window?.innerWidth || document.documentElement.clientWidth;
  const vHeight = window?.innerHeight || document.documentElement.clientHeight;
  if (x1 >= 0 && x2 <= vWidth && y1 >= 0 && y2 <= vHeight) {
    return true;
  }
  return false;
}
function isInTarget(target: HTMLElement, pos: number[]) {
  const [x1, x2, y1, y2] = pos;
  const { top, left, bottom, right } = target.getBoundingClientRect();
  if (x1 >= left && x2 <= right && y1 >= top && y2 <= bottom) {
    return true;
  }
  return false;
}

function isInOverlap(arr: HTMLElement[], pos: number[]) {
  const [x1, x2, y1, y2] = pos;
  for (const item of arr) {
    const { top, left, bottom, right } = item.getBoundingClientRect();
    if (x1 >= left && x2 <= right && y1 >= top && y2 <= bottom) {
      return true;
    }
  }
  return false;
}

function isInOverflowParent(
  overflowParentRect: {
    top: number;
    left: number;
    bottom: number;
    right: number;
  },
  pos: number[]
) {
  const [x1, x2, y1, y2] = pos;
  if (!overflowParentRect) {
    return true;
  }
  const { top, left, bottom, right } = overflowParentRect;
  if (x1 >= left && x2 <= right && y1 >= top && y2 <= bottom) {
    return true;
  }
  return false;
}

/**
 * 节点类型
 */
export const NODETYPE = {
  ELEMENT: 1 // 元素
};

export const checkHTMLElement = (node: Node): node is HTMLElement =>
  node && node.nodeType === NODETYPE.ELEMENT;

/**
 * 判断点位为c0：btm包含c0，且c0前面为空或以.结尾，c0后面为空或以.开始
 * @param btm btm点位
 * @returns c位为空
 */
export const isEmptyBtmC = (btm: string): boolean => /^(?:.*\.)?c0(?:\..*)?$/.test(btm);

/**
 * 判断点位为d0：btm包含d0，且d0前面为空或以.结尾，d0后面必须为空
 * @param btm btm点位
 * @returns d位为空
 */
export const isEmptyBtmD = (btm: string): boolean => /^(?:.*\.)?d0$/.test(btm);

export function findNearestAttribute(node: HTMLElement | null, attributeName: string): string {
  // 如果当前节点为空或已达到文档树的根节点，则返回null
  if (!node || node === document.documentElement) {
    return '{}';
  }

  // 获取当前节点的指定属性值
  const attributeValue = node.getAttribute(attributeName);

  // 如果当前节点有指定属性值，则返回该值
  if (attributeValue !== null) {
    return attributeValue;
  }

  // 否则，递归查找当前节点的父节点
  return findNearestAttribute(node.parentNode as HTMLElement, attributeName);
}
